import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  BookOpen,
  Hammer,
  Rocket,
  Activity,
  Server,
  DollarSign,
  BarChart3,
  Radio,
  PanelLeftClose,
  PanelLeftOpen,
  Zap,
  Power,
  Users,
  ScrollText,
  Building2,
} from 'lucide-react';
import { useUIStore } from '@/store/ui';
import { useAuthConfig } from '@/hooks/useAuthConfig';
import { useProfile } from '@/hooks/useProfile';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/services', icon: BookOpen, label: 'Service Catalog' },
  { to: '/builds', icon: Hammer, label: 'Builds' },
  { to: '/deployments', icon: Rocket, label: 'Deployments' },
  { to: '/observability', icon: Activity, label: 'Observability' },
  { to: '/infrastructure', icon: Server, label: 'Infrastructure' },
  // Cost Analytics now holds both the cloud-spend and unit-economics views as
  // tabs; /unit-costs still resolves for anyone with it bookmarked.
  { to: '/costs', icon: DollarSign, label: 'Cost Analytics' },
  { to: '/tenants', icon: Building2, label: 'Tenants' },
  { to: '/management', icon: BarChart3, label: 'Management' },
  { to: '/environments', icon: Power, label: 'Environments' },
];

const adminItems = [
  { to: '/admin/users', icon: Users, label: 'Users' },
  { to: '/admin/audit-logs', icon: ScrollText, label: 'Audit Logs' },
  { to: '/collectors', icon: Radio, label: 'Collectors' },
];

export default function Sidebar() {
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const { data: authConfig } = useAuthConfig();
  const { data: profile } = useProfile();
  const showAdminNav = !authConfig?.auth_enforced || profile?.role === 'admin';

  return (
    <aside
      className={clsx(
        'flex h-screen flex-col bg-white shadow-sidebar transition-all duration-300 ease-spring dark:bg-slate-900 dark:shadow-none dark:border-r dark:border-slate-800/60',
        sidebarCollapsed ? 'w-[4.25rem]' : 'w-[15.5rem]',
      )}
    >
      {/* Logo */}
      <div className="flex h-16 items-center gap-3 px-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-600 shadow-sm">
          <Zap className="h-[18px] w-[18px] text-white" strokeWidth={2.5} />
        </div>
        {!sidebarCollapsed && (
          <div className="flex flex-col">
            <span className="text-[15px] font-bold tracking-tight text-slate-900 dark:text-slate-100">
              Circuitry
            </span>
            <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
              Developer Portal
            </span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 pt-2 pb-4">
        <ul className="space-y-0.5">
          {navItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  clsx(
                    'sidebar-link group relative',
                    isActive && 'active',
                    sidebarCollapsed && 'justify-center px-0',
                  )
                }
                title={sidebarCollapsed ? item.label : undefined}
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-brand-600 dark:bg-brand-400" />
                    )}
                    <item.icon
                      className={clsx(
                        'h-[18px] w-[18px] shrink-0 transition-colors',
                        isActive
                          ? 'text-brand-600 dark:text-brand-400'
                          : 'text-slate-400 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300',
                      )}
                      strokeWidth={isActive ? 2.25 : 1.75}
                    />
                    {!sidebarCollapsed && <span>{item.label}</span>}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        {showAdminNav && (
          <>
            <div className="my-4 flex items-center gap-3 px-2">
              <div className="h-px flex-1 bg-slate-100 dark:bg-slate-800" />
              {!sidebarCollapsed && (
                <span className="select-none text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-300 dark:text-slate-600">
                  Admin
                </span>
              )}
              <div className="h-px flex-1 bg-slate-100 dark:bg-slate-800" />
            </div>

            <ul className="space-y-0.5">
              {adminItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      clsx(
                        'sidebar-link group relative',
                        isActive && 'active',
                        sidebarCollapsed && 'justify-center px-0',
                      )
                    }
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-brand-600 dark:bg-brand-400" />
                        )}
                        <item.icon
                          className={clsx(
                            'h-[18px] w-[18px] shrink-0 transition-colors',
                            isActive
                              ? 'text-brand-600 dark:text-brand-400'
                              : 'text-slate-400 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300',
                          )}
                          strokeWidth={isActive ? 2.25 : 1.75}
                        />
                        {!sidebarCollapsed && <span>{item.label}</span>}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </>
        )}
      </nav>

      {/* Collapse toggle */}
      <div className="border-t border-slate-100 p-3 dark:border-slate-800/60">
        <button
          onClick={toggleSidebar}
          className="flex w-full items-center justify-center rounded-lg p-2 text-slate-400 transition-all duration-200 hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800/60 dark:hover:text-slate-300"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <PanelLeftOpen className="h-[18px] w-[18px]" />
          ) : (
            <PanelLeftClose className="h-[18px] w-[18px]" />
          )}
        </button>
      </div>
    </aside>
  );
}
